import { CommonModule } from '@angular/common';
import { Component, EventEmitter, OnDestroy, OnInit, Output, ViewEncapsulation } from '@angular/core';
import { FormsModule } from '@angular/forms';

import { UiKitModule } from '../../../shared/modules/ui-kit/ui-kit.module';
import { ProntoPagoStateService } from '../../../services/pronto-pago-state.service';
import { ErrorEnvioCodigo, ProntoPagoRestitucionService, RespuestaCodigo } from '../../../services/pronto-pago-restitucion.service';

/**
 * Por qué se ofrece la verificación en sucursal:
 *   - `sin-acceso`: el afiliado declara que ese correo ya no es suyo.
 *   - `sin-correo`: no tiene correo registrado (el back no genera el código).
 *   - `limite-envios`: superó el máximo de envíos por hora que permite el back.
 *   - `envio-fallido`: se agotaron los envíos y el último también falló.
 */
export type MotivoSinAcceso = 'sin-acceso' | 'sin-correo' | 'limite-envios' | 'envio-fallido';

/**
 * Paso 2 de 2 del flujo "Pronto Pago": VERIFICACIÓN.
 *
 * Solicita el código de 8 caracteres enviado al correo del titular para firmar el acuerdo.
 * "Validar y firmar" valida el código (POST /validar-clave) y, si es correcto, registra la
 * aceptación con la modalidad elegida (POST /aceptar). Luego emite `firmado`.
 */
@Component({
  selector: 'app-pronto-pago-verificacion',
  standalone: true,
  imports: [CommonModule, FormsModule, UiKitModule],
  templateUrl: './verificacion.component.html',
  styleUrl: './verificacion.component.scss',
  encapsulation: ViewEncapsulation.None
})
export class VerificacionComponent implements OnInit, OnDestroy {

  @Output() volver = new EventEmitter<void>();
  @Output() firmado = new EventEmitter<void>();

  readonly pasoActual = 2;

  readonly enviosMaximos = 3;

  info?: RespuestaCodigo;
  codigo = '';
  intento = 1;
  error?: string;
  validando = false;
  errorEnvio = false;
  enviando = false;
  envios = 0;
  limiteEnvios = false;

  sinAcceso = false;
  motivoSinAcceso: MotivoSinAcceso = 'sin-acceso';

  segundosRestantes = 0;
  private timerId: any;

  constructor(
    private readonly prontoPagoService: ProntoPagoRestitucionService,
    private readonly estado: ProntoPagoStateService
  ) { }

  ngOnInit(): void {
    this.enviarCodigo();
  }

  ngOnDestroy(): void {
    this.detenerTimer();
  }

  enviarCodigo(): void {
    if (this.enviando) {
      return;
    }
    if (!this.quedanEnvios) {
      this.mostrarSinAcceso(this.limiteEnvios ? 'limite-envios' : 'envio-fallido');
      return;
    }

    this.envios++;
    const ultimoEnvio = !this.quedanEnvios;

    this.enviando = true;
    this.errorEnvio = false;

    this.prontoPagoService.enviarCodigoVerificacion().subscribe({
      next: info => {
        this.enviando = false;
        this.info = info;
        this.codigo = '';
        this.error = undefined;
        this.intento = 1;
        this.iniciarTimer(info.vigenciaSegundos);
      },
      error: (err: unknown) => {
        this.enviando = false;
        const motivo = (err as ErrorEnvioCodigo)?.motivo;

        if (motivo === 'sin-correo') {
          this.mostrarSinAcceso('sin-correo');
          return;
        }

        if (motivo === 'limite-envios') {
          // El back no generará más códigos en esta ventana: se retira el reenvío. Si ya hay un
          // código en curso se conserva la tarjeta, porque el afiliado puede tenerlo en su correo.
          this.limiteEnvios = true;
          this.envios = this.enviosMaximos;
          if (this.info) {
            this.errorEnvio = true;
          } else {
            this.mostrarSinAcceso('limite-envios');
          }
          return;
        }

        this.errorEnvio = true;

        if (ultimoEnvio) {
          this.mostrarSinAcceso('envio-fallido');
        }
      }
    });
  }

  get quedanEnvios(): boolean {
    return this.envios < this.enviosMaximos;
  }

  validar(): void {
    if (!this.puedeValidar) {
      return;
    }
    this.validando = true;
    this.error = undefined;
    this.prontoPagoService.validarCodigo(this.codigo).subscribe({
      next: res => {
        if (res.valido) {
          this.confirmarYContinuar();
        } else {
          this.validando = false;
          this.error = res.mensaje;
          this.intento++;
        }
      },
      error: () => {
        this.validando = false;
        this.error = 'No se pudo validar el código; intenta nuevamente.';
      }
    });
  }

  private iniciarTimer(segundos: number): void {
    this.detenerTimer();
    this.segundosRestantes = segundos;
    this.timerId = setInterval(() => {
      if (this.segundosRestantes > 0) {
        this.segundosRestantes--;
      } else {
        this.detenerTimer();
      }
    }, 1000);
  }

  private detenerTimer(): void {
    if (this.timerId) {
      clearInterval(this.timerId);
      this.timerId = undefined;
    }
  }

  get vigenciaFormateada(): string {
    const m = Math.floor(this.segundosRestantes / 60);
    const s = this.segundosRestantes % 60;
    return `${this.dosDigitos(m)}:${this.dosDigitos(s)}`;
  }

  private dosDigitos(n: number): string {
    return n < 10 ? `0${n}` : `${n}`;
  }

  get expirado(): boolean {
    return this.segundosRestantes <= 0;
  }

  get bloqueado(): boolean {
    return !!this.info && this.intento > this.info.intentosMaximos;
  }

  get intentoMostrado(): number {
    return this.info ? Math.min(this.intento, this.info.intentosMaximos) : this.intento;
  }

  get puedeValidar(): boolean {
    return !!this.info
      && this.codigo.length === this.info.largoCodigo
      && !this.bloqueado
      && !this.expirado
      && !this.validando;
  }

  private confirmarYContinuar(): void {
    const compensaDeuda = this.estado.codTipoDetalleSeleccionado === 1;
    this.prontoPagoService.aceptarOferta(compensaDeuda).subscribe({
      next: res => {
        if (!res.aceptado) {
          this.validando = false;
          this.error = 'No se pudo registrar la aceptación de la propuesta; intenta nuevamente.';
          return;
        }
        this.firmado.emit();
      },
      error: () => {
        this.validando = false;
        this.error = 'No se pudo registrar la aceptación de la propuesta; intenta nuevamente.';
      }
    });
  }

  noTengoAcceso(): void {
    this.mostrarSinAcceso('sin-acceso');
  }

  private mostrarSinAcceso(motivo: MotivoSinAcceso): void {
    this.motivoSinAcceso = motivo;
    this.sinAcceso = true;
    this.error = undefined;
    this.detenerTimer();
  }
}
